import { type FC, useState } from 'react'

import { Box, Section, Spinner } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import legalEntitiesCollection from 'modules/core/store/collections/legalEntities'
import app from 'modules/core/store/models/app'

import AddRoleLegalEntitiesModal from 'modules/job_catalog/components/AddRoleLegalEntitiesModal'
import ConfirmationModal from 'modules/job_catalog/components/ConfirmationModal'
import useRemoveRoleLegalEntity from 'modules/job_catalog/hooks/useRemoveRoleLegalEntity'
import { useRoleLegalEntities } from 'modules/job_catalog/hooks/useRoleLegalEntities'

import Item from './Item'

interface Props {
  roleId: string
}

const RoleLegalEntitiesForm: FC<Props> = ({ roleId }) => {
  const [legalEntities, isFetching, refetch] = useRoleLegalEntities(roleId)
  const removeRoleLegalEntity = useRemoveRoleLegalEntity()
  const [isAddLegalEntityModalOpen, setIsAddLegalEntityModalOpen] = useState(false)
  const [nextDeleteLegalEntityId, setNextDeleteLegalEntityId] = useState<string>()
  const unassignedLegalEntities = useMemo(
    () => legalEntitiesCollection.filter((x) => !legalEntities?.find((y) => y.id === x.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [legalEntities?.length]
  )

  const handleDeleteLegalEntity = async (legalEntityId: string) => {
    const result = await removeRoleLegalEntity(roleId, legalEntityId)
    const { success } = handleResult(result, () => undefined)

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.legal_entity_removed_successfully'),
      })
      setNextDeleteLegalEntityId(undefined)
      refetch()
    }
  }

  const handleLegalEntityDelete = (id: string) => {
    setNextDeleteLegalEntityId(id)
    track(trackingEvents.jobCatalog.deleteJobRoleLegalEntityStart)
  }

  const items = legalEntities?.map((x) => (
    <Item
      key={x.id}
      label={x.legalName}
      isDisabled={legalEntities.length === 1}
      onDelete={() => handleLegalEntityDelete(x.id)}
    />
  ))

  const handleAddLegalEntity = () => {
    setIsAddLegalEntityModalOpen(true)
    track(trackingEvents.jobCatalog.addJobRoleLegalEntityStart)
  }

  const handleRemoveLegalEntityConfirm = () => {
    nextDeleteLegalEntityId && handleDeleteLegalEntity(nextDeleteLegalEntityId)
    track(trackingEvents.jobCatalog.removeJobRoleLegalEntitySuccess)
  }

  const handleRemoveLegalEntityCancel = () => {
    setNextDeleteLegalEntityId(undefined)
    track(trackingEvents.jobCatalog.removeJobRoleLegalEntityCancel)
  }

  const handleModalClose = () => {
    refetch()
    setIsAddLegalEntityModalOpen(false)
  }

  return (
    <Section.List
      illustration="multiple-buildings"
      title={i18n.t('job_catalog.job_legal_entities_title')}
      description={i18n.t('job_catalog.job_legal_entities_description')}
      action={{
        label: i18n.t('job_catalog.job_legal_entities_action'),
        onClick: handleAddLegalEntity,
        disabled: !unassignedLegalEntities.length,
      }}
    >
      {isFetching && (
        <Box alignItems="center" paddingY="s24">
          <Spinner />
        </Box>
      )}
      {!!legalEntities?.length && !isFetching && items}
      {isAddLegalEntityModalOpen && (
        <AddRoleLegalEntitiesModal
          roleId={roleId}
          unasignedLegalEntities={unassignedLegalEntities}
          onClose={handleModalClose}
        />
      )}
      {nextDeleteLegalEntityId && (
        <ConfirmationModal
          title={i18n.t('job_catalog.unnasign_legal_entity_title')}
          description={i18n.t('job_catalog.unnasign_legal_entity_description')}
          onConfirm={handleRemoveLegalEntityConfirm}
          onCancel={handleRemoveLegalEntityCancel}
        />
      )}
    </Section.List>
  )
}

export default observer(RoleLegalEntitiesForm)
